import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowPathIcon, BanknotesIcon, CalendarDaysIcon, ChatBubbleLeftEllipsisIcon, CheckCircleIcon, ChevronRightIcon,
  ClipboardDocumentCheckIcon, DocumentTextIcon, ExclamationTriangleIcon, SparklesIcon, UserGroupIcon,
} from '@heroicons/react/24/outline';
import dashboardService from '../../services/dashboardService';
import { errText } from '../../services/scheduleService';
import { PreopBadge } from '../../components/preop/PreopPanel';
import { rands } from '../../components/billing/TariffLines';

// -----------------------------------------------------------------------------
// Dashboard — the web home page. A doctor sees their own day: today's lists with
// the booked patients, what's waiting on them (pre-ops back, billing queries,
// cases not yet billed) and this month's money. An administrator sees the same
// across the practice.
// -----------------------------------------------------------------------------

const TYPE = {
  theatre: { label: 'Theatre', tone: 'bg-blue-50 text-blue-700' },
  rooms: { label: 'Rooms', tone: 'bg-emerald-50 text-emerald-700' },
  on_call: { label: 'On call', tone: 'bg-violet-50 text-violet-700' },
  leave: { label: 'Away', tone: 'bg-slate-100 text-slate-500' },
};
const INVOICE = {
  preview: { label: 'Invoice not sent', tone: 'bg-amber-50 text-amber-800' },
  awaiting_review: { label: 'With billing', tone: 'bg-blue-50 text-blue-700' },
  query: { label: 'Billing query', tone: 'bg-red-50 text-red-700' },
  final: { label: 'Invoiced', tone: 'bg-green-50 text-green-700' },
};

const iso = (d) => new Date(`${d}T12:00:00`);
const fmtDay = (d) => iso(d).toLocaleDateString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short' });
const fmtLong = (d) => iso(d).toLocaleDateString('en-ZA', { weekday: 'long', day: 'numeric', month: 'long' });
const ageOf = (dob) => {
  if (!dob) return null;
  const b = new Date(`${dob}T12:00:00`);
  const n = new Date();
  return n.getFullYear() - b.getFullYear() - (n < new Date(n.getFullYear(), b.getMonth(), b.getDate()) ? 1 : 0);
};
const nameOf = (p) => [p.title, p.first_names, p.surname].filter(Boolean).join(' ');
const metaOf = (p) => [ageOf(p.date_of_birth) != null ? `${ageOf(p.date_of_birth)} y` : null, p.sex, p.payer === 'private' ? 'Private' : p.scheme_name].filter(Boolean).join(' · ');
const timeOf = (b) => (b.bstart ? `${b.bstart}–${b.bend}${b.btype === 'on_call' && b.bend <= b.bstart ? ' +1' : ''}` : b.btype === 'on_call' ? 'All day' : '');
const placeOf = (b) => b.hshort || b.hname || TYPE[b.btype]?.label;
const billed = (p) => !!p.draft_id && p.invoice_status !== 'discarded';
const greeting = () => {
  const h = Number(new Date().toLocaleString('en-ZA', { hour: '2-digit', hour12: false, timeZone: 'Africa/Johannesburg' }));
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
};
const compactRands = (n) => (Math.abs(n) >= 100000 ? `R ${(n / 1000).toLocaleString('en-ZA', { maximumFractionDigits: 0 })}k` : rands(n).replace(/[.,]00$/, ''));

const Card = ({ title, icon: Icon, action, children, className = '' }) => (
  <section className={`overflow-hidden rounded-xl border border-slate-200 bg-white ${className}`}>
    <header className="flex items-center justify-between gap-2 border-b border-slate-100 px-5 py-3">
      <h2 className="flex items-center gap-2 font-bold text-slate-800">{Icon && <Icon className="h-5 w-5 text-slate-400" />}{title}</h2>
      {action}
    </header>
    {children}
  </section>
);

// Stat tile: label · value · one line of context. Values stay in text ink; only the
// warning line carries a status colour, and never without its icon.
const Tile = ({ label, value, sub, warn, onClick }) => (
  <button onClick={onClick} className="group rounded-xl border border-slate-200 bg-white px-4 py-3 text-left transition hover:border-slate-300 hover:shadow-sm">
    <div className="flex items-center justify-between text-xs font-semibold text-slate-500">
      {label}<ChevronRightIcon className="h-4 w-4 text-slate-300 transition group-hover:text-slate-500" />
    </div>
    <div className="mt-1 text-2xl font-semibold text-slate-900">{value}</div>
    <div className={`mt-0.5 flex items-center gap-1 text-xs ${warn ? 'font-semibold text-red-700' : 'text-slate-500'}`}>
      {warn && <ExclamationTriangleIcon className="h-3.5 w-3.5" />}{sub}
    </div>
  </button>
);

const Pill = ({ tone, children }) => <span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ${tone}`}>{children}</span>;

const Dashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await dashboardService.get());
      setError('');
    } catch (e) {
      setError(errText(e));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  // Back to the tab after billing on the phone → fresh figures.
  useEffect(() => {
    const onFocus = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onFocus);
    return () => document.removeEventListener('visibilitychange', onFocus);
  }, [load]);

  // The same hand-off as the schedule's Bill button: the assistant opens with the booking and patient.
  const bill = (session, p) => navigate('/assistant', {
    state: {
      billing: {
        key: `${session.bid}-${p.patient_id}-${Date.now()}`,
        bid: session.bid,
        patient_id: p.patient_id,
        patient_name: nameOf(p),
        patient_dob: p.date_of_birth,
        patient_sex: p.sex,
        payer_text: p.payer === 'private' ? 'Private' : p.scheme_name || null,
        date: session.bdate,
        hospital: session.hshort || session.hname || null,
        theatre: session.btheatre || null,
        surgeon: session.bsurgeon || null,
        planned_procedure: p.planned_procedure || null,
      },
    },
  });
  const openPreop = (q) => navigate('/preop', { state: { view: 'to_review', open: q } });
  const openInvoice = (tab, id) => navigate('/billing', { state: { tab, openId: id } });

  if (!data) {
    return (
      <div className="mx-auto max-w-7xl p-8">
        <h1 className="text-2xl font-bold text-brand-deep">Dashboard</h1>
        {error ? <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div> : <p className="mt-4 text-slate-400">Loading your day…</p>}
      </div>
    );
  }

  const practice = data.scope === 'practice';
  const todays = data.sessions.filter((s) => s.bdate === data.today);
  const later = data.sessions.filter((s) => s.bdate !== data.today && s.btype !== 'leave');
  const todayPatients = todays.reduce((n, s) => n + s.patients.length, 0);
  const toFinish = (data.claims.drafts?.count || 0) + (data.claims.queries?.count || 0);
  const nothingWaiting = !data.preop.items.length && !data.queries.length && !data.drafts.length && !data.unbilled.length;

  const laterByDay = later.reduce((m, s) => { (m[s.bdate] = m[s.bdate] || []).push(s); return m; }, {});

  return (
    <div className="mx-auto max-w-7xl p-8">
      {/* Greeting */}
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-brand-deep">{greeting()}, {data.me.name}</h1>
          <p className="mt-1 text-slate-500">
            {fmtLong(data.today)}{practice ? ' · the whole practice' : ''}
            {data.unread ? <> · <b className="text-slate-700">{data.unread} unread notice{data.unread === 1 ? '' : 's'}</b></> : null}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={load} disabled={loading} title="Refresh" className="rounded-lg border border-slate-300 bg-white p-2 text-slate-500 hover:bg-slate-50 disabled:opacity-50">
            <ArrowPathIcon className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <Link to="/schedule" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
            <CalendarDaysIcon className="h-4 w-4" /> Schedule
          </Link>
          <Link to="/assistant" className="inline-flex items-center gap-1.5 rounded-lg bg-brand-gradient px-4 py-2 text-sm font-semibold text-white">
            <SparklesIcon className="h-4 w-4" /> Ask the assistant
          </Link>
        </div>
      </div>

      {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      {/* Headline numbers */}
      <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
        <Tile
          label="Patients today"
          value={todayPatients}
          sub={todays.length ? `${todays.length} session${todays.length === 1 ? '' : 's'}` : 'No sessions today'}
          onClick={() => document.getElementById('today')?.scrollIntoView({ behavior: 'smooth' })}
        />
        <Tile
          label="Cases to bill"
          value={data.unbilled.length >= 50 ? '50+' : data.unbilled.length}
          sub="Booked in the last 30 days"
          onClick={() => document.getElementById('attention')?.scrollIntoView({ behavior: 'smooth' })}
        />
        <Tile
          label="Pre-ops to review"
          value={data.preop.to_review}
          sub={data.preop.with_alerts ? `${data.preop.with_alerts} with alerts` : `${data.preop.waiting} waiting for patients`}
          warn={data.preop.with_alerts > 0}
          onClick={() => navigate('/preop', { state: { view: 'to_review' } })}
        />
        <Tile
          label="Invoices to finish"
          value={toFinish}
          sub={data.claims.queries?.count ? `${data.claims.queries.count} queried by billing` : 'Not yet sent to billing'}
          warn={data.claims.queries?.count > 0}
          onClick={() => openInvoice(data.claims.queries?.count ? 'queries' : 'drafts')}
        />
        <Tile
          label="Outstanding"
          value={compactRands(data.claims.outstanding)}
          sub={`This month ${compactRands(data.month.billed)} billed · ${compactRands(data.month.paid)} paid`}
          onClick={() => openInvoice('submitted')}
        />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_24rem]">
        <div className="min-w-0 space-y-6">
          {/* Today */}
          <Card title={`Today · ${fmtDay(data.today)}`} icon={CalendarDaysIcon} action={<Link to="/schedule" className="text-sm font-semibold text-brand-teal">Open schedule</Link>}>
            <div id="today" />
            {todays.length === 0 && (
              <div className="px-5 py-8 text-center text-sm text-slate-500">
                No sessions today.
                {later[0] && <> Next: <b className="text-slate-700">{fmtDay(later[0].bdate)}</b> · {placeOf(later[0])} {timeOf(later[0])}</>}
              </div>
            )}
            {todays.map((s) => (
              <div key={s.bid} className="border-b border-slate-100 last:border-0">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 bg-slate-50/70 px-5 py-2.5">
                  <span className="w-28 text-sm font-bold text-brand-navy">{timeOf(s) || '—'}</span>
                  <span className="font-semibold text-slate-800">{s.btype === 'leave' ? s.bnotes || 'Away' : placeOf(s)}</span>
                  {s.btheatre && <span className="text-sm text-slate-500">{/^\d+$/.test(s.btheatre) ? `Theatre ${s.btheatre}` : s.btheatre}</span>}
                  {s.bsurgeon && <span className="text-sm text-slate-500">· {s.bsurgeon}</span>}
                  <Pill tone={TYPE[s.btype].tone}>{TYPE[s.btype].label}</Pill>
                  {practice && <span className="ml-auto text-sm text-slate-500">{s.doctor}</span>}
                </div>
                {s.btype !== 'leave' && s.patients.length === 0 && (
                  <p className="px-5 py-3 text-sm text-slate-400">No patients on this list yet — add them on the schedule.</p>
                )}
                {s.patients.length > 0 && (
                  <table className="w-full text-sm">
                    <tbody className="divide-y divide-slate-100">
                      {s.patients.map((p, i) => (
                        <tr key={p.patient_id} className="align-top">
                          <td className="w-10 py-2.5 pl-5 text-slate-400">{i + 1}</td>
                          <td className="py-2.5 pr-3">
                            <div className="font-semibold text-slate-800">{nameOf(p)}</div>
                            <div className="text-xs text-slate-500">{metaOf(p)}</div>
                          </td>
                          <td className="hidden py-2.5 pr-3 text-slate-600 md:table-cell">{p.planned_procedure || <span className="text-slate-300">—</span>}</td>
                          <td className="py-2.5 pr-3">
                            <div className="flex flex-wrap gap-1">
                              {p.preop ? <PreopBadge preop={p.preop} /> : <Pill tone="bg-slate-100 text-slate-500">No pre-op</Pill>}
                              {p.quote?.status === 'accepted' && <Pill tone="bg-green-50 text-green-700">Quote accepted</Pill>}
                              {p.quote?.status === 'declined' && <Pill tone="bg-red-50 text-red-700">Quote declined</Pill>}
                            </div>
                          </td>
                          <td className="py-2.5 pr-5 text-right">
                            {billed(p) ? (
                              <button onClick={() => openInvoice(p.invoice_status === 'query' ? 'queries' : p.invoice_status === 'preview' ? 'drafts' : 'all', p.draft_id)} className="inline-flex">
                                <Pill tone={(INVOICE[p.invoice_status] || INVOICE.final).tone}>{p.invoice_no || (INVOICE[p.invoice_status] || INVOICE.final).label}</Pill>
                              </button>
                            ) : (
                              <button onClick={() => bill(s, p)} className="rounded-lg border border-brand-teal px-3 py-1 text-xs font-semibold text-brand-teal hover:bg-teal-50">Bill</button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ))}
          </Card>

          {/* The rest of the week */}
          <Card title="Coming up" icon={UserGroupIcon}>
            {Object.keys(laterByDay).length === 0 && <p className="px-5 py-6 text-center text-sm text-slate-400">Nothing else booked this week.</p>}
            <ul className="divide-y divide-slate-100">
              {Object.entries(laterByDay).map(([day, list]) => (
                <li key={day} className="flex gap-4 px-5 py-3">
                  <div className="w-24 shrink-0 text-sm font-bold text-slate-700">{fmtDay(day)}</div>
                  <div className="min-w-0 flex-1 space-y-1.5">
                    {list.map((s) => {
                      const back = s.patients.filter((p) => ['completed', 'reviewed'].includes(p.preop?.status)).length;
                      const alerts = s.patients.reduce((n, p) => n + (p.preop?.status === 'completed' ? p.preop.alert_count || 0 : 0), 0);
                      return (
                        <div key={s.bid} className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                          <span className="font-semibold text-slate-800">{placeOf(s)}</span>
                          <span className="text-slate-500">{timeOf(s)}</span>
                          {practice && <span className="text-slate-500">· {s.doctor}</span>}
                          <span className="text-slate-500">· {s.patients.length} patient{s.patients.length === 1 ? '' : 's'}</span>
                          {s.patients.length > 0 && <span className="text-slate-500">· pre-ops {back}/{s.patients.length} back</span>}
                          {alerts > 0 && <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-700"><ExclamationTriangleIcon className="h-3.5 w-3.5" />{alerts} alert{alerts === 1 ? '' : 's'}</span>}
                        </div>
                      );
                    })}
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        </div>

        {/* Needs attention */}
        <div id="attention" className="space-y-6">
          {nothingWaiting && (
            <div className="flex items-center gap-3 rounded-xl border border-green-200 bg-green-50 px-5 py-4 text-sm text-green-800">
              <CheckCircleIcon className="h-6 w-6" /> Nothing waiting on you.
            </div>
          )}

          {data.preop.items.length > 0 && (
            <Card title="Pre-ops back" icon={ClipboardDocumentCheckIcon} action={<span className="text-xs text-slate-400">{data.preop.to_review} to review</span>}>
              <ul className="divide-y divide-slate-100">
                {data.preop.items.map((q) => (
                  <li key={q.preop_id}>
                    <button onClick={() => openPreop(q)} className="w-full px-5 py-3 text-left hover:bg-slate-50">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold text-slate-800">{q.patient_name}</span>
                        <span className="text-xs text-slate-500">{fmtDay(q.service_date)}</span>
                      </div>
                      {practice && <div className="text-xs text-slate-500">{q.doctor}</div>}
                      {q.alerts.length > 0 ? (
                        <ul className="mt-1 space-y-0.5">
                          {q.alerts.slice(0, 2).map((a) => (
                            <li key={a} className="flex gap-1 text-xs text-red-700"><ExclamationTriangleIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" /><span className="line-clamp-1">{a}</span></li>
                          ))}
                          {q.alerts.length > 2 && <li className="pl-4 text-xs text-slate-500">+{q.alerts.length - 2} more</li>}
                        </ul>
                      ) : (
                        <div className="mt-0.5 text-xs text-slate-500">{q.caution_count ? `${q.caution_count} to check` : 'Nothing flagged'}</div>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {data.queries.length > 0 && (
            <Card title="Billing queries" icon={ChatBubbleLeftEllipsisIcon}>
              <ul className="divide-y divide-slate-100">
                {data.queries.map((d) => (
                  <li key={d.draft_id}>
                    <button onClick={() => openInvoice('queries', d.draft_id)} className="w-full px-5 py-3 text-left hover:bg-slate-50">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold text-slate-800">{d.patient_name || 'No patient linked'}</span>
                        <span className="text-xs text-slate-500">{fmtDay(d.service_date)}</span>
                      </div>
                      {d.query_note && <p className="mt-0.5 line-clamp-2 text-xs text-slate-600">“{d.query_note}”</p>}
                    </button>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {data.unbilled.length > 0 && (
            <Card title="Cases to bill" icon={BanknotesIcon} action={<span className="text-xs text-slate-400">{data.unbilled.length >= 50 ? '50+' : data.unbilled.length}</span>}>
              <ul className="divide-y divide-slate-100">
                {data.unbilled.slice(0, 8).map((p) => (
                  <li key={`${p.bid}-${p.patient_id}`} className="flex items-center gap-3 px-5 py-2.5">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold text-slate-800">{nameOf(p)}</div>
                      <div className="truncate text-xs text-slate-500">
                        {fmtDay(p.bdate)} · {placeOf(p)}{p.planned_procedure ? ` · ${p.planned_procedure}` : ''}{practice ? ` · ${p.doctor}` : ''}
                      </div>
                    </div>
                    <button onClick={() => bill(p, p)} className="rounded-lg border border-brand-teal px-3 py-1 text-xs font-semibold text-brand-teal hover:bg-teal-50">Bill</button>
                  </li>
                ))}
              </ul>
              {data.unbilled.length > 8 && <p className="border-t border-slate-100 px-5 py-2 text-xs text-slate-500">…and {data.unbilled.length - 8} more on the schedule.</p>}
            </Card>
          )}

          {data.drafts.length > 0 && (
            <Card title="Invoices not sent to billing" icon={DocumentTextIcon}>
              <ul className="divide-y divide-slate-100">
                {data.drafts.map((d) => (
                  <li key={d.draft_id}>
                    <button onClick={() => openInvoice('drafts', d.draft_id)} className="flex w-full items-center gap-3 px-5 py-2.5 text-left hover:bg-slate-50">
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-semibold text-slate-800">{d.patient_name || 'No patient linked'}</div>
                        <div className="truncate text-xs text-slate-500">{fmtDay(d.service_date)}{d.hospital_name ? ` · ${d.hospital_name}` : ''}{practice ? ` · ${d.doctor}` : ''}</div>
                      </div>
                      <span className="text-sm text-slate-700">{d.total == null ? '—' : rands(d.total)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
